import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { type DateValues, displayFormat, GameDates, ManagePrizes } from '@pkg/components/Sections/Admin';
import { GameValues, requiredValuesFields } from '@pkg/components/Sections/Admin/Fantasy/GameForm/GameValues';
import { LabelledInput } from '@pkg/components/Sections/Admin/components/Form';
import { StyledCheckbox, StyledForm, StyledLabel } from '@pkg/components/Sections/Admin/components/Form/styles';
import { StyledToggleHeading } from '@pkg/components/Sections/Admin/components/StyledComponents/styles';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { Sizes } from '@pkg/components/Sections/Profile/Buttons/Button/types';

import {
    type Competition,
    type CreateCompetition,
    type PatchCompetitionParams,
    type Prize,
} from '@app/client/common/structs';
import {
    DEFAULT_COMPETITION_FORM_VALUES,
    formattedDate,
    formattedPostDate,
    handleInputValue,
    handleValueValidation,
    missingRequiredFields,
    useFormErrors,
} from '@app/helpers';

import { Typography, Wrapper } from '../../../../DesignSystem';

import { ButtonWrapper } from './styles';

type GameFormProps = {
    onSuccess?: () => void;
    isDuplicate?: boolean;
    onUpdate?: (postData: PatchCompetitionParams) => void;
    onCreate?: (postData: CreateCompetition) => void;
    existingGame?: Competition;
};

const dateFields = ['submission_starts_at', 'submission_ends_at', 'starts_at', 'ends_at'];

const requiredFields = [...dateFields, ...requiredValuesFields, 'name'];

export const GameForm = ({ onCreate, onUpdate, existingGame, isDuplicate = false }: GameFormProps) => {
    const { t } = useTranslation('admin_fantasy');
    const [showPrizes, setShowPrizes] = useState(true);
    const [showGameCriteria, setShowGameCriteria] = useState(true);
    const [showOtherFields, setShowOtherFields] = useState(true);

    const [publishOnSubmit, setPublishOnSubmit] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const { formErrors, handleError } = useFormErrors({ fieldNames: requiredFields });

    const getInitial = () => {
        if (existingGame) {
            const nonNull = { ...existingGame };
            Object.keys(nonNull).forEach((k) => {
                const val = nonNull[k];
                nonNull[k] = val === null ? '' : val;
            });
            return {
                ...nonNull,
                prizes: existingGame.prizes ?? [],
                team_prizes: existingGame.team_prizes ?? [],
                dynamic_criteria: existingGame?.dynamic_criteria
                    ? [...DEFAULT_COMPETITION_FORM_VALUES.dynamic_criteria, ...existingGame.dynamic_criteria]
                    : DEFAULT_COMPETITION_FORM_VALUES.dynamic_criteria,
            };
        } else return DEFAULT_COMPETITION_FORM_VALUES;
    };

    const [rewards, setRewards] = useState<Prize[]>(existingGame?.prizes ?? []);
    const [teamRewards, setTeamRewards] = useState<Prize[]>(existingGame?.team_prizes ?? []);
    const [formData, setFormData] = useState<CreateCompetition>(getInitial());

    useEffect(() => {
        const disabled = Boolean(rewards?.length < 1) || missingRequiredFields(requiredFields, formData);
        setSubmitDisabled(disabled);
    }, [formData, rewards?.length]);

    const handleSetFormData = useCallback((newValue, name?) => {
        if (name) {
            setFormData((fd) => ({ ...fd, [name]: newValue }));
        } else {
            setFormData((fd) => ({ ...fd, ...newValue }));
        }
    }, []);

    function handleDateValues(dateValues: DateValues) {
        const formValues = {
            submission_starts_at: dateValues.submission_starts_at,
            submission_ends_at: dateValues.submissions,
            ends_at: dateValues.game,
        };
        handleSetFormData(formValues);
    }
    function handleCriteriaValues(name, newValue) {
        handleSetFormData(newValue, name);
    }
    function handleInputValues(e) {
        const { type, name, required } = e.target;
        const value = handleInputValue(e);

        if (required && type !== 'datetime-local') {
            const error = handleValueValidation(e);
            handleError(name, error);
        }
        handleSetFormData(value, name);
    }

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();

            const published_at = formData?.published_at ? formData?.published_at : formattedDate();
            const submitData = {
                ...formData,
                prizes: rewards,
                team_prizes: teamRewards,
                published_at: publishOnSubmit ? formattedPostDate(published_at) : undefined,
            };
            if (isDuplicate) {
                submitData['id'] = undefined;
                submitData['slug'] = undefined;
            }

            if (existingGame && !isDuplicate) {
                onUpdate?.(submitData);
            } else {
                onCreate?.(submitData);
            }
        },
        [existingGame, formData, isDuplicate, onCreate, onUpdate, publishOnSubmit, rewards, teamRewards],
    );

    const detailMarkup = showOtherFields && (
        <Wrapper>
            <LabelledInput
                onChange={handleInputValues}
                type="text"
                name="description"
                id="description"
                value={formData.description || ''}
                label={t('create_label_description')}
                placeholder={t('create_placeholder_description')}
            />
            <Wrapper direction="row">
                <Button to="rules" color="transparent">{`${existingGame ? 'Manage' : 'View default'} Rules`}</Button>
                <Button to="faq" color="transparent">{`${existingGame ? 'Manage' : 'View default'} FAQ`}</Button>
            </Wrapper>

            <Wrapper direction="row">
                <Wrapper padding="0 10px">
                    <LabelledInput
                        onChange={handleInputValues}
                        name="game_label"
                        id="game_label"
                        value={formData.game_label}
                        label={t('create_label_game_label')}
                        placeholder={t('create_placeholder_label')}
                        type="text"
                    />
                    <LabelledInput
                        onChange={handleInputValues}
                        name="landing_page_url"
                        id="landing_page_url"
                        value={formData.landing_page_url || ''}
                        label={t('create_label_url')}
                        placeholder={t('create_placeholder_url')}
                        type="url"
                    />
                </Wrapper>
                <Wrapper padding="0 10px">
                    <LabelledInput
                        onChange={handleInputValues}
                        name="sponsor"
                        id="sponsor"
                        value={formData.sponsor || ''}
                        label={t('create_label_sponsor')}
                        placeholder={t('create_placeholder_sponsor')}
                        type="text"
                    />
                    <LabelledInput
                        onChange={handleInputValues}
                        name="sponsor_logo"
                        id="sponsor_logo"
                        value={formData.sponsor_logo || ''}
                        label={t('create_label_sponsor_logo')}
                        placeholder={t('create_placeholder_sponsor_logo')}
                        type="url"
                    />
                </Wrapper>
                <Wrapper padding="0 10px">
                    <LabelledInput
                        onChange={(e) => handleSetFormData([e.target.value], 'header_images')}
                        name="game_header_image"
                        id="game_header_image"
                        value={formData.header_images?.[0] || ''}
                        label={t('create_label_game_header_image')}
                        type="text"
                    />
                </Wrapper>
            </Wrapper>
        </Wrapper>
    );

    const prizeModalMarkup = showPrizes && (
        <Wrapper direction="row" justifyContent="space-betweem" gap="20px">
            <ManagePrizes rewardType="player" setRewards={setRewards} rewards={rewards} />
            <ManagePrizes rewardType="team" setRewards={setTeamRewards} rewards={teamRewards} />
        </Wrapper>
    );

    const checkboxMarkup =
        !existingGame ||
        (!existingGame?.published_at && (
            <Wrapper padding="10px 0">
                <StyledLabel htmlFor="published_at">
                    <StyledCheckbox
                        type="checkbox"
                        name="published_at"
                        id="published_at"
                        checked={publishOnSubmit}
                        onChange={(e) => setPublishOnSubmit(e.target.checked)}
                    />
                    <Typography type="label" size={20}>
                        {t('create_checkbox_publish')}
                    </Typography>
                </StyledLabel>
            </Wrapper>
        ));

    const requiredFieldMarkup = (
        <Wrapper>
            <h3 style={{ color: 'red', margin: 0 }}>
                <strong>UTC TIME IS BEING USED HERE </strong>
            </h3>
            <Wrapper direction="row" justifyContent="space-between">
                <Typography type="paragraph" size={16}>
                    ⏰ Current: <strong>{formattedDate(undefined, displayFormat)}</strong>
                </Typography>
                <Typography type="paragraph" size={16}>
                    Details in this section are required 🔔
                </Typography>
            </Wrapper>
            <hr />
            <Wrapper direction="row" flexWrap="wrap" gap="10px">
                <Wrapper width="45%" margin="10px 0">
                    <LabelledInput
                        required
                        onChange={handleInputValues}
                        name="name"
                        id="name"
                        value={formData.name}
                        label={t('create_label_name')}
                        placeholder={t('create_placeholder_name')}
                        type="text"
                        errorMessage={formErrors['name']}
                    />
                </Wrapper>
                <GameDates onUpdate={handleDateValues} gameData={existingGame} isDuplicate={isDuplicate} />
                <GameValues onUpdate={handleCriteriaValues} gameData={existingGame} />
            </Wrapper>
        </Wrapper>
    );

    const getCriteriaValue = (category: string, field: string) => {
        const criteria = formData?.dynamic_criteria?.find((criteria) => Boolean(criteria[category]));
        if (criteria) return criteria?.[category]?.[field];
    };

    const setCriteriaValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = handleInputValue(event);
        const category = event.target.name;
        const field = event.target.dataset['field'];

        setFormData((currentFormData) => {
            const newFormData = { ...currentFormData };
            newFormData.dynamic_criteria = newFormData?.dynamic_criteria?.map((criteria) => {
                if (criteria[category]) {
                    criteria[category][field] = parseFloat(value);
                }
                return criteria;
            });
            return newFormData;
        });
    };

    const gameCriteria = showGameCriteria && (
        <Wrapper direction="row" gap="15px">
            <Wrapper>
                <LabelledInput
                    required
                    data-field="amount"
                    onChange={setCriteriaValue}
                    name="MinimumAssetCount"
                    id="MinimumAssetCount"
                    value={getCriteriaValue('MinimumAssetCount', 'amount')}
                    label={t('create_label_dynamic_criteria_minimum_collection_tokens')}
                    type="number"
                />
                <LabelledInput
                    required
                    data-field="amount"
                    onChange={setCriteriaValue}
                    name="AptosMinimumAssetCount"
                    id="AptosMinimumAssetCount"
                    value={getCriteriaValue('AptosMinimumAssetCount', 'amount')}
                    label={t('create_label_dynamic_criteria_aptos_minimum_collection_tokens')}
                    type="number"
                />
                <LabelledInput
                    required
                    data-field="amount"
                    onChange={setCriteriaValue}
                    name="PolygonMinimumAssetCount"
                    id="PolygonMinimumAssetCount"
                    value={getCriteriaValue('PolygonMinimumAssetCount', 'amount')}
                    label={t('create_label_dynamic_criteria_polygon_minimum_collection_tokens')}
                    type="number"
                />
                <LabelledInput
                    required
                    data-field="percent"
                    onChange={setCriteriaValue}
                    name="MinimumAssetsOnSalePercentage"
                    id="MinimumAssetsOnSalePercentage"
                    value={getCriteriaValue('MinimumAssetsOnSalePercentage', 'percent')}
                    label={t('create_label_dynamic_criteria_minimum_tokens_on_sale')}
                    type="number"
                />
                <LabelledInput
                    required
                    data-field="percent"
                    onChange={setCriteriaValue}
                    name="AptosMinimumAssetsOnSalePercentage"
                    id="AptosMinimumAssetsOnSalePercentage"
                    value={getCriteriaValue('AptosMinimumAssetsOnSalePercentage', 'percent')}
                    label={t('create_label_dynamic_criteria_aptos_minimum_tokens_on_sale')}
                    type="number"
                />
                <LabelledInput
                    required
                    data-field="percent"
                    onChange={setCriteriaValue}
                    name="PolygonMinimumAssetsOnSalePercentage"
                    id="PolygonMinimumAssetsOnSalePercentage"
                    value={getCriteriaValue('PolygonMinimumAssetsOnSalePercentage', 'percent')}
                    label={t('create_label_dynamic_criteria_polygon_minimum_tokens_on_sale')}
                    type="number"
                />
            </Wrapper>

            <Wrapper>
                <LabelledInput
                    required
                    data-field="percent"
                    onChange={setCriteriaValue}
                    name="MinimumUniqueCollectionOwnerPercentage"
                    id="MinimumUniqueCollectionOwnerPercentage"
                    value={getCriteriaValue('MinimumUniqueCollectionOwnerPercentage', 'percent')}
                    label={t('create_label_dynamic_criteria_percent_owners')}
                    type="number"
                />
                <LabelledInput
                    required
                    data-field="percent"
                    onChange={setCriteriaValue}
                    name="AptosMinimumUniqueCollectionOwnerPercentage"
                    id="AptosMinimumUniqueCollectionOwnerPercentage"
                    value={getCriteriaValue('AptosMinimumUniqueCollectionOwnerPercentage', 'percent')}
                    label={t('create_label_dynamic_criteria_aptos_percent_owners')}
                    type="number"
                />
                <LabelledInput
                    required
                    data-field="percent"
                    onChange={setCriteriaValue}
                    name="PolygonMinimumUniqueCollectionOwnerPercentage"
                    id="PolygonMinimumUniqueCollectionOwnerPercentage"
                    value={getCriteriaValue('PolygonMinimumUniqueCollectionOwnerPercentage', 'percent')}
                    label={t('create_label_dynamic_criteria_polygon_percent_owners')}
                    type="number"
                />
                <LabelledInput
                    required
                    onChange={setCriteriaValue}
                    data-field="amount"
                    name="MinimumSalesCount"
                    id="MinimumSalesCount"
                    value={getCriteriaValue('MinimumSalesCount', 'amount')}
                    label={t('create_label_dynamic_criteria_minimum_sales')}
                    type="number"
                />
                <LabelledInput
                    required
                    onChange={setCriteriaValue}
                    data-field="amount"
                    name="AptosMinimumSalesCount"
                    id="AptosMinimumSalesCount"
                    value={getCriteriaValue('AptosMinimumSalesCount', 'amount')}
                    label={t('create_label_dynamic_criteria_aptos_minimum_sales')}
                    type="number"
                />
                <LabelledInput
                    required
                    onChange={setCriteriaValue}
                    data-field="amount"
                    name="PolygonMinimumSalesCount"
                    id="PolygonMinimumSalesCount"
                    value={getCriteriaValue('PolygonMinimumSalesCount', 'amount')}
                    label={t('create_label_dynamic_criteria_polygon_minimum_sales')}
                    type="number"
                />
            </Wrapper>
        </Wrapper>
    );

    return (
        <Wrapper>
            <StyledForm onSubmit={(e) => handleSubmit(e)}>
                <Wrapper>
                    {requiredFieldMarkup}
                    <hr />
                    <Wrapper>
                        <Wrapper direction="row" justifyContent="space-between">
                            <Wrapper width="50%">
                                <StyledToggleHeading type="button" onClick={() => setShowPrizes((e) => !e)}>
                                    <Typography type="headline" size={20}>
                                        {`${showPrizes ? 'Collapse' : 'Expand'} Prizes`}
                                    </Typography>
                                </StyledToggleHeading>
                            </Wrapper>
                            <Typography type="paragraph" size={16}>
                                At least one prize is required to publish a game 🚨
                            </Typography>
                        </Wrapper>
                        {prizeModalMarkup}
                        <hr />
                        <StyledToggleHeading onClick={() => setShowGameCriteria(!showGameCriteria)} type="button">
                            <Typography type="headline" size={20}>
                                {showGameCriteria ? `Collapse Game Criteria` : `Expand Game Criteria`}
                            </Typography>
                        </StyledToggleHeading>
                        {gameCriteria}
                        <hr />
                        <StyledToggleHeading onClick={() => setShowOtherFields(!showOtherFields)} type="button">
                            <Typography type="headline" size={20}>
                                {showOtherFields ? `Collapse other details` : `Expand other details`}
                            </Typography>
                        </StyledToggleHeading>
                        {detailMarkup}
                    </Wrapper>
                </Wrapper>
                <ButtonWrapper>
                    {checkboxMarkup}
                    <Button type="submit" size={Sizes.small} disabled={submitDisabled}>
                        {t(existingGame && !isDuplicate ? 'create_cta_save' : 'create_cta_create')}
                    </Button>
                </ButtonWrapper>
            </StyledForm>
        </Wrapper>
    );
};
